import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { EmptyState, fmtMoney } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  UsersThree,
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  ArrowsMerge,
  Warning,
  Percent,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const blank = { name: "", email: "", phone: "", commission_pct: 0, notes: "", aliases: [] };

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Editor
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank);
  const [aliasInput, setAliasInput] = useState("");

  // Suggestions (typo merger)
  const [sugOpen, setSugOpen] = useState(false);
  const [sugLoading, setSugLoading] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeNewName, setMergeNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agents");
      setAgents(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    if (!search.trim()) return agents;
    const q = search.toLowerCase();
    return agents.filter(
      (a) =>
        (a.name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q) ||
        (a.aliases || []).some((al) => al.toLowerCase().includes(q))
    );
  }, [agents, search]);

  const stats = useMemo(() => {
    const totalMRR = agents.reduce((s, a) => s + Number(a.mrr || 0), 0);
    const totalActive = agents.reduce((s, a) => s + Number(a.active_clients || 0), 0);
    return { totalMRR, totalActive, totalAgents: agents.length };
  }, [agents]);

  const startCreate = () => {
    setEditId(null);
    setForm(blank);
    setAliasInput("");
    setOpen(true);
  };
  const startEdit = (a) => {
    setEditId(a.id);
    setForm({
      name: a.name || "",
      email: a.email || "",
      phone: a.phone || "",
      commission_pct: a.commission_pct || 0,
      notes: a.notes || "",
      aliases: a.aliases || [],
    });
    setAliasInput("");
    setOpen(true);
  };

  const addAlias = () => {
    const v = aliasInput.trim();
    if (!v) return;
    if (form.aliases.includes(v)) {
      setAliasInput("");
      return;
    }
    setForm({ ...form, aliases: [...form.aliases, v] });
    setAliasInput("");
  };
  const removeAlias = (a) => setForm({ ...form, aliases: form.aliases.filter((x) => x !== a) });

  const save = async () => {
    try {
      const payload = {
        ...form,
        commission_pct: Number(form.commission_pct) || 0,
      };
      if (editId) await api.put(`/agents/${editId}`, payload);
      else await api.post("/agents", payload);
      toast.success(editId ? "Agente actualizado" : "Agente creado");
      setOpen(false);
      setForm(blank);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    if (!confirm(`¿Eliminar al agente "${a.name}"?\n\nLos clientes vinculados a este agente perderán la referencia (el nombre libre del referrer se conserva).`)) return;
    try {
      await api.delete(`/agents/${a.id}`);
      toast.success("Agente eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openSuggestions = async () => {
    setSugOpen(true);
    setSugLoading(true);
    try {
      const { data } = await api.get("/agents/referrer-suggestions");
      setClusters(data.clusters || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSugLoading(false);
    }
  };

  const startMerge = (cluster) => {
    setSelectedCluster(cluster);
    if (cluster.existing_agent_id) {
      setMergeTargetId(cluster.existing_agent_id);
      setMergeNewName("");
    } else {
      setMergeTargetId("");
      setMergeNewName(cluster.canonical);
    }
  };

  const executeMerge = async () => {
    if (!selectedCluster) return;
    const raw_names = selectedCluster.variants.map((v) => v.name);
    try {
      const payload = mergeTargetId
        ? { target_agent_id: mergeTargetId, raw_names }
        : { new_agent_name: mergeNewName.trim(), raw_names };
      if (!mergeTargetId && !mergeNewName.trim()) {
        toast.error("Escribe el nombre del agente");
        return;
      }
      const { data } = await api.post("/agents/merge", payload);
      toast.success(`Fusionado · ${data.clients_updated} clientes vinculados`);
      setSelectedCluster(null);
      openSuggestions();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const autoMergeAll = async () => {
    if (!confirm(`¿Auto-fusionar los ${clusters.length} clusters detectados?\n\n· Crea automáticamente los agentes que no existen\n· Vincula todos los clientes con referrer resuelto\n· Los que ya coinciden con un agente existente se agregan como aliases\n\nEsto se puede revertir manualmente después.`)) return;
    try {
      const { data } = await api.post("/agents/auto-merge-all");
      toast.success(`Auto-fusión: ${data.agents_created} agentes creados · ${data.clients_updated} clientes vinculados · ${data.clusters_processed} clusters procesados`);
      setSugOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <PageShell
      section="Administración"
      page="Agentes / Referidos"
      context={`${agents.length} agentes · ${stats.totalActive} clientes vinculados`}
      testid="agents-toolbar"
      actions={
        <div className="flex items-center gap-2">
          <Button
            onClick={openSuggestions}
            variant="outline"
            data-testid="agents-suggestions"
            className="h-9 text-xs border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowsMerge size={14} className="mr-1.5" />
            Resolver referidos
          </Button>
          <Button
            onClick={startCreate}
            data-testid="agents-new"
            className="h-9 bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus size={15} className="mr-1.5" />
            Nuevo agente
          </Button>
        </div>
      }
    >
      {/* Summary strip */}
      <div className="mb-5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200 dark:divide-slate-800">
          <div className="px-5 py-4 relative">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-slate-900 dark:bg-slate-100" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">Agentes</div>
            <div className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-slate-900 dark:text-slate-100">{stats.totalAgents}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">perfiles registrados</div>
          </div>
          <div className="px-5 py-4 relative">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-emerald-500" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">Clientes vinculados</div>
            <div className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-slate-900 dark:text-slate-100">{stats.totalActive}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">activos con referrer resuelto</div>
          </div>
          <div className="px-5 py-4 relative">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-amber-500" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">MRR referido</div>
            <div className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-slate-900 dark:text-slate-100">{fmtMoney(stats.totalMRR)}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">ingreso mensual generado</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-3">
        <div className="relative max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, email o alias…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
            data-testid="agents-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Cargando…</div>
        ) : visible.length === 0 ? (
          <EmptyState
            icon={UsersThree}
            title={agents.length === 0 ? "Sin agentes" : "Sin resultados"}
            message={
              agents.length === 0
                ? "Crea perfiles para los referentes/agentes de ventas. Puedes fusionar automáticamente typos existentes desde \"Resolver referidos\"."
                : "Ajusta la búsqueda o los filtros."
            }
            action={
              agents.length === 0 ? (
                <div className="flex items-center gap-2">
                  <Button onClick={openSuggestions} variant="outline" className="h-9 text-xs">
                    <ArrowsMerge size={14} className="mr-1.5" />
                    Resolver referidos existentes
                  </Button>
                  <Button onClick={startCreate} className="h-9 bg-slate-900 hover:bg-slate-800 text-white">
                    <Plus size={15} className="mr-1.5" />
                    Nuevo agente
                  </Button>
                </div>
              ) : null
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="agents-table">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Agente</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Contacto</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Alias</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Clientes</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">MRR</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Comisión</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((a) => (
                  <tr key={a.id} data-testid={`agent-row-${a.id}`} className="border-b border-slate-100 dark:border-slate-900 hover:bg-slate-50/50 dark:hover:bg-slate-900/40">
                    <td className="px-4 py-3 align-middle">
                      <div className="font-semibold text-slate-900 dark:text-slate-100">{a.name}</div>
                      {a.notes && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1 max-w-[220px]">{a.notes}</div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-middle text-[12px] text-slate-600 dark:text-slate-400">
                      <div>{a.email || "—"}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{a.phone || ""}</div>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(a.aliases || []).slice(0, 3).map((al) => (
                          <span key={al} className="inline-block text-[10px] px-1.5 py-0.5 rounded-sm border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400">
                            {al}
                          </span>
                        ))}
                        {(a.aliases || []).length > 3 && (
                          <span className="text-[10px] text-slate-500">+{a.aliases.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-middle text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
                      {a.active_clients || 0}
                      {a.total_clients > a.active_clients && (
                        <span className="text-[10px] text-slate-500 ml-1">/ {a.total_clients}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 align-middle text-right font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                      {fmtMoney(a.mrr)}
                    </td>
                    <td className="px-3 py-3 align-middle text-right font-mono tabular-nums text-slate-700 dark:text-slate-300">
                      {(a.commission_pct || 0).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 align-middle text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => startEdit(a)}
                          data-testid={`agent-edit-${a.id}`}
                          className="p-1.5 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                          title="Editar"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          onClick={() => remove(a)}
                          data-testid={`agent-delete-${a.id}`}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Editor dialog ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950" data-testid="agent-form-dialog">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                {editId ? "Editar" : "Nuevo"} · Agente
              </div>
              {editId && form.name && (
                <>
                  <div className="h-3 w-px bg-slate-300 dark:bg-slate-700" />
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[280px]">{form.name}</div>
                </>
              )}
            </div>
          </div>

          <div className="px-5 py-4">
            <div className="grid grid-cols-12 gap-x-4 gap-y-3">
              <div className="col-span-12 sm:col-span-7 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Nombre completo *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" data-testid="agent-name" />
              </div>
              <div className="col-span-12 sm:col-span-5 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Comisión (%)</Label>
                <div className="relative">
                  <Input type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className="h-9 text-sm font-mono tabular-nums pr-8 border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" data-testid="agent-commission" />
                  <Percent size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>
              <div className="col-span-12 sm:col-span-7 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
              </div>
              <div className="col-span-12 sm:col-span-5 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
              </div>

              <div className="col-span-12 flex items-center gap-2 pt-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500 font-semibold">Alias (variantes)</div>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
              </div>

              <div className="col-span-12 space-y-1">
                <div className="flex items-center gap-2">
                  <Input
                    value={aliasInput}
                    onChange={(e) => setAliasInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }}
                    placeholder="ej: Flavio Padron"
                    className="h-9 text-sm flex-1 border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                    data-testid="agent-alias-input"
                  />
                  <Button onClick={addAlias} variant="outline" className="h-9 text-xs">
                    <Plus size={13} className="mr-1" />
                    Añadir
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2 min-h-[24px]">
                  {form.aliases.length === 0 && <span className="text-[11px] text-slate-400 italic">Sin alias · añade los nombres tal como aparecen en el CSV</span>}
                  {form.aliases.map((al) => (
                    <button
                      key={al}
                      onClick={() => removeAlias(al)}
                      className="group inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-sm border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-rose-300 hover:text-rose-600 transition-colors"
                    >
                      {al}
                      <Trash size={10} className="opacity-40 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="col-span-12 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Notas</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 resize-none" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
              Los alias vinculan los referrer del CSV con este agente
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="h-8 text-xs">Cancelar</Button>
              <Button onClick={save} disabled={!form.name.trim()} className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white" data-testid="agent-save">
                {editId ? "Guardar cambios" : "Crear agente"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Suggestions / merge dialog ── */}
      <Dialog open={sugOpen} onOpenChange={(v) => { setSugOpen(v); if (!v) setSelectedCluster(null); }}>
        <DialogContent className="max-w-3xl p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950" data-testid="agents-suggestions-dialog">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
              Resolver referidos · CSV → Agentes
            </div>
          </div>

          <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">
            {sugLoading ? (
              <div className="text-center py-8 text-sm text-slate-500">Analizando referidos…</div>
            ) : clusters.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle size={32} weight="fill" className="text-emerald-500 mx-auto mb-2" />
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Todo resuelto</div>
                <div className="text-[12px] text-slate-500 mt-1">No hay referrers pendientes de mapear a agentes.</div>
              </div>
            ) : (
              <>
                <div className="mb-3 flex items-start justify-between gap-3 px-3 py-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/60 rounded">
                  <div className="flex items-start gap-2 flex-1 text-[12px] text-blue-800 dark:text-blue-300">
                    <Warning size={14} className="mt-0.5 shrink-0" />
                    <span>Se detectaron <strong className="mx-1">{clusters.length}</strong> grupos de referrers.
                    Puedes fusionar cluster por cluster abajo, o auto-fusionar todo de una vez.</span>
                  </div>
                  <Button
                    onClick={autoMergeAll}
                    data-testid="auto-merge-all"
                    className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white shrink-0"
                  >
                    <ArrowsMerge size={12} className="mr-1" />
                    Auto-fusionar todos ({clusters.length})
                  </Button>
                </div>

                <div className="space-y-2">
                  {clusters.map((c) => {
                    const isSelected = selectedCluster?.canonical === c.canonical;
                    return (
                      <div
                        key={c.canonical}
                        data-testid={`cluster-${c.canonical}`}
                        className={`border rounded-md transition-colors ${
                          isSelected
                            ? "border-slate-900 dark:border-slate-100 bg-slate-50 dark:bg-slate-900"
                            : "border-slate-200 dark:border-slate-800 hover:border-slate-300"
                        }`}
                      >
                        <div className="px-4 py-3 flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="font-semibold text-slate-900 dark:text-slate-100">{c.canonical}</div>
                              <span className="text-[11px] text-slate-500">
                                {c.total_clients} cliente{c.total_clients !== 1 ? "s" : ""}
                              </span>
                              {c.existing_agent_id && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-400">
                                  <CheckCircle size={10} weight="fill" />
                                  Sugerencia: {c.existing_agent_name}
                                </span>
                              )}
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {c.variants.map((v) => (
                                <span key={v.name} className="inline-block text-[11px] px-1.5 py-0.5 rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-400">
                                  {v.name} <span className="text-slate-400">·{v.count}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                          <Button
                            onClick={() => startMerge(c)}
                            variant={isSelected ? "default" : "outline"}
                            className={`h-8 text-xs shrink-0 ${isSelected ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}`}
                            data-testid={`cluster-merge-${c.canonical}`}
                          >
                            <ArrowsMerge size={12} className="mr-1" />
                            Fusionar
                          </Button>
                        </div>

                        {isSelected && (
                          <div className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-3 space-y-3">
                            <div className="space-y-1">
                              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                Vincular a agente existente
                              </Label>
                              <select
                                value={mergeTargetId}
                                onChange={(e) => setMergeTargetId(e.target.value)}
                                className="w-full h-9 text-sm px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 focus:outline-none"
                              >
                                <option value="">— Crear agente nuevo —</option>
                                {agents.map((a) => (
                                  <option key={a.id} value={a.id}>{a.name}</option>
                                ))}
                              </select>
                            </div>
                            {!mergeTargetId && (
                              <div className="space-y-1">
                                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                  Nombre del nuevo agente
                                </Label>
                                <Input
                                  value={mergeNewName}
                                  onChange={(e) => setMergeNewName(e.target.value)}
                                  className="h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                                  placeholder="ej: Flavio Padron"
                                />
                              </div>
                            )}
                            <div className="flex items-center justify-end gap-2 pt-1">
                              <Button variant="outline" onClick={() => setSelectedCluster(null)} className="h-8 text-xs">Cancelar</Button>
                              <Button onClick={executeMerge} className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white" data-testid="execute-merge">
                                Confirmar fusión ({c.total_clients} cliente{c.total_clients !== 1 ? "s" : ""})
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-end px-5 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <Button variant="outline" onClick={() => setSugOpen(false)} className="h-8 text-xs">Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
